/**
 * The one entry point to a model, and the one place the app's cost rules apply.
 *
 * Everything in `contract.ts` is re-exported from here, so a caller imports the
 * seam and the dispatcher together and no file has to know that there are three
 * adapters behind it.
 */

import { resolveEffort, stampOf } from '../core/models'
import type { ModelCallJsonValue, ModelCallReceipt } from '../core/types'
import { beforePaidCall } from '../core/durability'
import type { TextCallObservation } from './observation'
import { ModelError, retriesFor } from './providers/contract'
import type { StreamOptions, StreamResult, TextOptions, TextResult } from './providers/contract'

export * from './providers/contract'

/**
 * How long one request may run before it is presumed hung.
 *
 * This bounds the one genuinely unbounded state in the app. Every other ceiling —
 * moves, tokens, dollars — is tested BETWEEN passes, so none of them can fire
 * while a pass is stuck inside an await: a request that opens and never finishes
 * holds `busy`, and an unattended run, forever. Stop still works, because a fetch
 * abort rejects even a socket gone silent — but Stop needs a person present, and
 * the whole premise of a run is that nobody is.
 *
 * Ten minutes, recalibrated the expensive way: the first shipped value was six,
 * reasoned as 2× the slowest pass then on record (a 170-second audit) — and the
 * next broad seed produced an honest GPT expand still streaming thought at 6:00,
 * which the deadline killed. The asymmetry this number serves: killing a paid
 * pass falsely costs real money (the provider bills the reasoning the ledger
 * never sees), while waiting extra minutes for a genuinely hung one costs
 * minutes. So it leans well past the longest HONEST pass ever observed, and a
 * timeout is a verdict that the request is stuck — never a tax on a slow model.
 */
export const PASS_TIMEOUT_MS = 10 * 60_000

/**
 * Run one adapter call under the deadline.
 *
 * The adapter sees only the inner signal; the caller's is forwarded into it. On
 * deadline the inner controller aborts with NO custom reason, deliberately — both
 * adapters classify a plain abort as `aborted` by reading the signal itself and
 * their SDK's `APIUserAbortError`, so the deadline rides exactly the path the Stop
 * button rides, and nothing SDK-shaped can tell them apart. (anthropic.ts once
 * went by the error's name, which its SDK never sets, so an Anthropic deadline
 * reached this function as `network` and was never reclassified.)
 *
 * This function can, and it is the only place that can: the deadline fired and
 * the caller never aborted, so this was a hang and not a hand. It rethrows as
 * `timeout`, carrying whatever the pass had already billed. Everything downstream
 * then does the right thing for free — `reportError` falls through to the message
 * instead of "Stopped.", the op records an error, and a run counts it toward
 * `unreachable` exactly as it counts a request that failed outright.
 *
 * Exported for its tests; callers use `streamObjects` and `streamText`.
 */
export async function withDeadline<T>(
  outer: AbortSignal | undefined,
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const inner = new AbortController()
  const forward = () => inner.abort()
  if (outer?.aborted) inner.abort()
  else outer?.addEventListener('abort', forward, { once: true })
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    inner.abort()
  }, PASS_TIMEOUT_MS)
  try {
    return await run(inner.signal)
  } catch (err) {
    // Reclassified only when the deadline was the cause: it fired, the caller
    // never asked to stop, and the adapter read the abort as a cancellation.
    if (timedOut && !outer?.aborted && err instanceof ModelError && err.kind === 'aborted') {
      throw new ModelError(
        `No answer for ${Math.round(PASS_TIMEOUT_MS / 60_000)} minutes, so the pass was abandoned rather than left hanging.`,
        'timeout',
        err.spent,
      )
    }
    throw err
  } finally {
    clearTimeout(timer)
    outer?.removeEventListener('abort', forward)
  }
}

/**
 * Run one pass. Unchanged in name and shape from the single-provider build.
 *
 * Three lines of policy — lower the effort, clamp the output ceiling, pick an
 * adapter — so both adapters receive an already-legal request and neither
 * re-derives the app's rules. This is also the ONE place `resolveEffort` is
 * called: a clamp that every caller has to remember is a clamp that one caller
 * will forget, and the pinned `high` on a source check and the pinned `xhigh` on a
 * breakthrough scan are exactly the calls that would forget it.
 *
 * The adapters are DYNAMICALLY imported. Statically, the OpenAI SDK alone adds
 * ~150 kB minified with nothing tree-shakeable — `new OpenAI()` reaches chat
 * completions, assistants, vector stores and threads — and a first-run visitor who
 * never pastes a key would pay all of it to watch the demo. This function is
 * already async and is about to make a network call, so the await is free. The
 * Google adapter carries no SDK and is imported the same way regardless: one
 * rule for where an adapter loads is a rule nobody has to remember.
 */
interface ModelCallReceiptTarget {
  id: string
  ordinal: number
  record: (receipt: ModelCallReceipt) => void
}

async function dispatchObjects(
  o: StreamOptions,
  receiptTarget?: ModelCallReceiptTarget,
): Promise<StreamResult> {
  await beforePaidCall()
  const e = o.model.entry
  const effort = resolveEffort(e, o.effort)
  const maxTokens = e.maxOutput
    ? Math.min(o.maxTokens ?? 16000, e.maxOutput)
    : (o.maxTokens ?? 16000)
  const startedAt = Date.now()
  const schema = structuredClone(o.schema.schema) as ModelCallJsonValue
  const receiptBase = receiptTarget
    ? {
        version: 1 as const,
        id: receiptTarget.id,
        ordinal: receiptTarget.ordinal,
        kind: 'structured' as const,
        messages: [
          { role: 'system' as const, content: o.system },
          { role: 'user' as const, content: o.user },
        ] as const,
        parameters: {
          schema: { name: o.schema.name, value: schema },
          effort,
          maxTokens,
          rootArrayKey: o.rootArrayKey ?? null,
          web: o.web
            ? {
                maxSearches: o.web.maxSearches,
                maxFetches: o.web.maxFetches,
                maxContentTokens: o.web.maxContentTokens ?? null,
              }
            : null,
          cacheKey: o.cacheKey ?? null,
          maxRetries: retriesFor(o),
        },
        selectedModel: stampOf(e),
        startedAt,
      }
    : null
  let adapterCompleted = false
  try {
    const result = await withDeadline(o.signal, async (signal) => {
      const opts: StreamOptions = {
        ...o,
        signal,
        effort,
        maxTokens,
      }
      if (e.provider === 'anthropic')
        return (await import('./providers/anthropic')).runAnthropic(opts)
      if (e.provider === 'google') return (await import('./providers/google')).runGoogle(opts)
      // OpenAI and a custom endpoint share one adapter, because "OpenAI-compatible" is
      // the shape every proxy, gateway and local server speaks. The only difference
      // reaching the adapter is `model.baseURL`.
      return (await import('./providers/openai')).runOpenAI(opts)
    })
    adapterCompleted = true
    if (receiptBase && receiptTarget) {
      receiptTarget.record({
        ...receiptBase,
        answeredModel: result.spend.model,
        completedAt: Date.now(),
        outcome: result.refused
          ? {
              kind: 'refused',
              note: result.refusalNote ?? null,
              truncated: result.truncated,
            }
          : { kind: 'completed', truncated: result.truncated },
        usage: result.spend,
      })
    }
    return result
  } catch (error) {
    // A receipt sink can fail after a paid response succeeded. Do not replace
    // that successful usage with a second fabricated provider-failure receipt.
    if (adapterCompleted) throw error
    if (receiptBase && receiptTarget) {
      receiptTarget.record({
        ...receiptBase,
        // A failed stream may carry a billing stamp before it carries proof that
        // a model answered. Usage keeps that stamp; authorship stays unknown.
        answeredModel: null,
        completedAt: Date.now(),
        outcome: {
          kind: 'failed',
          errorKind: error instanceof ModelError ? error.kind : 'unknown',
          reason: error instanceof Error ? error.message : 'Unknown model-call failure.',
        },
        usage: error instanceof ModelError ? (error.spent ?? null) : null,
      })
    }
    throw error
  }
}

export async function streamObjects(o: StreamOptions): Promise<StreamResult> {
  return dispatchObjects(o)
}

/**
 * Run one operation-owned adapter invocation and retain the post-render request.
 * The adapter's retries (on the SDKs' terms — `nextAttempt`) and provider
 * continuations may occur inside this boundary.
 *
 * The ordinary `streamObjects` stays receipt-free for evaluation and other
 * callers that have no durable operation. This entry point makes attribution an
 * explicit input and records failures after the deadline has classified them.
 */
export async function streamObjectsWithReceipt(
  o: StreamOptions,
  receiptTarget: ModelCallReceiptTarget,
): Promise<StreamResult> {
  return dispatchObjects(o, receiptTarget)
}

/**
 * Run one prose turn. The same three lines of policy, the same three adapters.
 *
 * Separate from `streamObjects` rather than a flag on it, because the two differ
 * in what they are allowed to do and not only in what they return: this one
 * declares no tools and constrains no shape, and keeping that in the type is what
 * stops a conversation quietly gaining a search it must not have.
 */
export async function streamText(o: TextOptions): Promise<TextResult> {
  return dispatchText(o)
}

/** Opt-in exact prose receipts for an investigation evaluation. Ordinary chat
 * does not acquire a second persistent ledger merely because evaluation exists. */
export async function streamTextWithObservation(
  o: TextOptions,
  record: (observation: TextCallObservation) => void,
): Promise<TextResult> {
  return dispatchText(o, record)
}

/** Prose uses the same operation ledger as structured calls, with an explicit
 * null schema and its own exact cache blocks/history. */
export async function streamTextWithReceipt(
  o: TextOptions,
  target: ModelCallReceiptTarget,
): Promise<TextResult> {
  return dispatchText(o, (observation) =>
    target.record({
      version: 1,
      id: target.id,
      ordinal: target.ordinal,
      kind: 'text',
      messages: [
        { role: 'system', content: observation.system },
        { role: 'user', content: observation.user },
      ],
      parameters: {
        ...observation.parameters,
        schema: null,
        rootArrayKey: null,
        web: null,
        cacheKey: o.cacheKey ?? null,
      },
      textContext: {
        ...(observation.systemDynamic !== undefined
          ? { systemDynamic: observation.systemDynamic }
          : {}),
        ...(observation.systemTail !== undefined ? { systemTail: observation.systemTail } : {}),
        history: observation.history,
        output: observation.output,
        internalAttempts: null,
      },
      selectedModel: observation.selectedModel,
      answeredModel: observation.answeredModel,
      startedAt: observation.startedAt,
      completedAt: observation.completedAt,
      outcome:
        observation.outcome === 'failed'
          ? {
              kind: 'failed',
              errorKind: observation.errorKind ?? 'unknown',
              reason: `Text invocation failed: ${observation.errorKind ?? 'unknown'}.`,
            }
          : observation.outcome === 'refused'
            ? {
                kind: 'refused',
                note: observation.refusalNote ?? null,
                truncated: observation.truncated ?? false,
              }
            : { kind: 'completed', truncated: observation.truncated ?? false },
      usage: observation.usage,
    }),
  )
}

async function dispatchText(
  o: TextOptions,
  record?: (observation: TextCallObservation) => void,
): Promise<TextResult> {
  await beforePaidCall()
  const e = o.model.entry
  const startedAt = Date.now()
  const effort = resolveEffort(e, o.effort)
  const maxTokens = Math.min(o.maxTokens ?? 2000, e.maxOutput ?? Infinity)
  let output = ''
  const base = {
    version: 1 as const,
    boundary: 'adapter-invocation' as const,
    kind: 'text' as const,
    system: o.system,
    ...(o.systemDynamic !== undefined ? { systemDynamic: o.systemDynamic } : {}),
    ...(o.systemTail !== undefined ? { systemTail: o.systemTail } : {}),
    history: structuredClone(o.history ?? []),
    user: o.user,
    parameters: { effort, maxTokens, maxRetries: retriesFor(o), cacheKey: o.cacheKey ?? null },
    selectedModel: stampOf(e),
    startedAt,
    internalAttempts: null,
  }
  // Under the same deadline as the passes. A conversation turn is capped at a
  // fraction of a pass's output, so the deadline is generous here by a wide
  // margin — and a hung chat holds `busy` exactly the way a hung pass does.
  let adapterCompleted = false
  try {
    const result = await withDeadline(o.signal, async (signal) => {
      const opts: TextOptions = {
        ...o,
        signal,
        effort,
        maxTokens,
        onText: (delta) => {
          if (record) output += delta
          o.onText(delta)
        },
      }
      if (e.provider === 'anthropic') {
        return (await import('./providers/anthropic')).runAnthropicText(opts)
      }
      if (e.provider === 'google') {
        return (await import('./providers/google')).runGoogleText(opts)
      }
      return (await import('./providers/openai')).runOpenAIText(opts)
    })
    adapterCompleted = true
    record?.({
      ...base,
      answeredModel: result.spend.model,
      completedAt: Date.now(),
      outcome: result.refused ? 'refused' : 'completed',
      refusalNote: result.refusalNote ?? null,
      errorKind: null,
      usage: result.spend,
      output: result.text,
      truncated: result.truncated,
    })
    return result
  } catch (error) {
    if (adapterCompleted) throw error
    record?.({
      ...base,
      answeredModel: null,
      completedAt: Date.now(),
      outcome: 'failed',
      errorKind: error instanceof ModelError ? error.kind : 'unknown',
      usage: error instanceof ModelError ? (error.spent ?? null) : null,
      output,
      truncated: null,
    })
    throw error
  }
}
